/**
 * Module-level state holder for maintenance mode.
 *
 * Both React (via useSyncExternalStore) and non-React code (axios interceptor,
 * QueryClient onError) read and write through this module.
 */

export type MaintenanceStatus = 'ready' | 'maintenance' | 'connection' | 'offline';

export interface MaintenanceState {
  status: MaintenanceStatus;
  customMessage: string;
  lastChangedAt: number;
}

// Sentry is loaded as a global at runtime (when wired up); breadcrumbs are
// fire-and-forget so we read defensively. The Window.Sentry shape is
// declared in `src/vite-env.d.ts` so other consumers can use the same
// typed accessor without re-casting.
let state: MaintenanceState = {
  status: 'ready',
  customMessage: '',
  lastChangedAt: 0,
};

const listeners = new Set<() => void>();

function notify() {
  // Each subscriber is invoked under its own try/catch so that a single
  // throwing subscriber can't block the rest of the chain or bubble up
  // through setState -> runProbe -> maybeProbe and surface as an
  // unhandled rejection on the `void maybeProbe(...)` callsites. Mirrors
  // the Sentry resilience already documented around setState (a broken
  // observer must not break state propagation).
  listeners.forEach(fn => {
    try {
      fn();
    } catch (e) {
      // Fire-and-forget breadcrumb so operator dashboards see the
      // failure mode. Wrapped because Sentry itself can throw.
      if (typeof window !== 'undefined') {
        try {
          window.Sentry?.addBreadcrumb?.({
            category: 'maintenance',
            message: 'subscriber threw during notify',
            level: 'warning',
            data: { error: (e as Error)?.message ?? String(e) },
          });
        } catch {
          // Sentry SDK throwing must never break notify either.
        }
      }
    }
  });
}

/**
 * Returns the current maintenance state. The return type is `Readonly` to
 * signal that consumers must not mutate the returned object directly; all
 * updates go through `setState`. The runtime value is the same shared
 * reference as the internal state, so spreading or copying is unnecessary
 * for read access.
 */
export function getState(): Readonly<MaintenanceState> {
  return state;
}

/**
 * Caller-facing input shape for `setState`. Intentionally narrower than
 * `Partial<MaintenanceState>`:
 *
 * - `lastChangedAt` is excluded because the setter always stamps it with
 *   `Date.now()`; accepting it from callers would let the wrong value through
 *   without any signal that it's being discarded.
 * - Optional fields are runtime-filtered (see setState below) so that an
 *   explicit `undefined` value (which TypeScript permits when
 *   `exactOptionalPropertyTypes` is off, the default) cannot overwrite a
 *   valid field. Without that filter, a caller passing
 *   `setState({ customMessage: maybeString })` where `maybeString` is
 *   `undefined` at runtime would set `customMessage` to `undefined`, and
 *   downstream `customMessage.trim()` calls in MaintenanceState.tsx would
 *   crash.
 */
interface SetStateInput {
  status?: MaintenanceStatus;
  customMessage?: string;
}

/**
 * Update maintenance state and notify subscribers. Adds a Sentry breadcrumb
 * for observability when Sentry is loaded as a global; no-op otherwise.
 *
 * No-op short-circuit: if neither `status` nor `customMessage` would change,
 * setState returns immediately without touching `lastChangedAt`, emitting a
 * Sentry breadcrumb, or notifying subscribers. This matters because the
 * recovery poller calls `maybeProbe('poller')` every 30s while in a
 * maintenance window. Each tick that re-affirms `'maintenance'` would
 * otherwise produce a redundant `state: maintenance -> maintenance` breadcrumb,
 * a wasted re-render through `useSyncExternalStore`, and a misleading
 * `lastChangedAt` bump.
 *
 * Sentry-resilience: the breadcrumb call is wrapped in `try/catch` so a
 * misbehaving Sentry SDK (e.g., transport corruption, hub mismatch, lazy-load
 * race) cannot reject `setState` and leave the takeover unmounted during a
 * real outage. State propagation is the load-bearing path; observability is
 * fire-and-forget.
 */
export function setState(next: SetStateInput): void {
  const prevStatus = state.status;
  const prevMessage = state.customMessage;

  // Resolve the post-update values before mutating, so we can detect a no-op
  // without doing the spread first. Conditional resolution preserves the
  // existing `undefined`-filter contract: a caller passing
  // `setState({ status: undefined })` keeps the current status.
  const nextStatus = next.status !== undefined ? next.status : prevStatus;
  const nextMessage = next.customMessage !== undefined ? next.customMessage : prevMessage;

  if (nextStatus === prevStatus && nextMessage === prevMessage) {
    return;
  }

  state = {
    status: nextStatus,
    customMessage: nextMessage,
    lastChangedAt: Date.now(),
  };

  if (typeof window !== 'undefined') {
    try {
      window.Sentry?.addBreadcrumb?.({
        category: 'maintenance',
        message: `state: ${prevStatus} -> ${nextStatus}`,
        level: 'info',
        data: { customMessage: nextMessage },
      });
    } catch {
      // Swallow: state has already been updated and subscribers below will
      // be notified. A broken Sentry must not break the maintenance UI.
    }
  }
  notify();
}

export function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}
